import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MobileDialog } from "../components/mobile";
import axios from "axios";
import { toast } from "sonner";
import {
  Sun, Shield, Sparkles, Heart, Target, Check, Play, Loader2, X,
  TrendingUp, AlertTriangle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import StickAPinPrompt from "../components/pins/StickAPinPrompt";
import PullThePinPrompt from "../components/pins/PullThePinPrompt";

const API = process.env.REACT_APP_BACKEND_URL;

// Lesson prep videos are pulled by topic from the prep card; while approved
// content is being curated, video URLs are set to null so the iframe is
// suppressed. The text guidance below remains visible. Re-populate `video`
// once each clip has been reviewed and approved by the founder.
const PREP_CONTENT = {
  Roundabouts: {
    video: null,
    points: ["Approach slowly", "Choose correct lane early", "Observe traffic flow", "Commit when safe"],
  },
  Junctions: {
    video: null,
    points: ["Read the road", "Look both ways twice", "Signal clearly", "Wait for a safe gap"],
  },
  "Junction approach": {
    video: null,
    points: ["Read the road", "Look both ways twice", "Signal clearly", "Wait for a safe gap"],
  },
  Manoeuvres: {
    video: null,
    points: ["Slow clutch control", "Check mirrors and blind spots", "Accuracy over speed"],
  },
  "Clutch control": {
    video: null,
    points: ["Find the bite point", "Smooth releases", "Slow speed control"],
  },
  "Mirror checks": {
    video: null,
    points: ["Mirrors before signals", "Check before every action", "Blind spots matter"],
  },
};

/**
 * LearnerOpeningFlow — the guided support surface that greets every learner on /learn.
 * Features:
 *  1. Today's Check-In prompt (pulls /api/checkin/today)
 *  2. Red Button (one-tap "not with it" signal)
 *  3. Adaptive Confidence Message
 *  4. Lesson Prep card (personalised from next_lesson_focus)
 *  5. Today's Focus alignment for the NEXT upcoming lesson
 */
export default function LearnerOpeningFlow() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [checkin, setCheckin] = useState(null);
  const [redButton, setRedButton] = useState(null);
  const [adaptive, setAdaptive] = useState(null);
  const [pupilData, setPupilData] = useState(null);
  const [prepDismissed, setPrepDismissed] = useState(false);
  const [nextLesson, setNextLesson] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [ckRes, rbRes, adRes, ppRes, nlRes] = await Promise.all([
        axios.get(`${API}/api/checkin/today`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/api/red-button/today`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/api/adaptive/message`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/api/work-mode/pupil/dashboard`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/api/lesson-events/next`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setCheckin(ckRes.data);
      setRedButton(rbRes.data);
      setAdaptive(adRes.data);
      setPupilData(ppRes.data);
      setNextLesson(nlRes.data?.has_next ? nlRes.data : null);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) return null;

  return (
    <div className="space-y-4" data-testid="learner-opening-flow">
      {/* 1. Today's Check-In prompt */}
      {checkin && !checkin.completed_today && (
        <div data-testid="checkin-prompt"
          className="bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-900/15 dark:to-blue-900/10 border border-sky-200 dark:border-sky-800 rounded-2xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center flex-shrink-0">
              <Sun className="w-5 h-5 text-sky-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-extrabold text-slate-900 dark:text-white">How are you today? Quick check-in first.</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                One minute. Helps your instructor shape the lesson around you.
              </p>
              <Button data-testid="start-checkin" onClick={() => nav("/daily-checkin")}
                className="mt-3 h-9 bg-sky-600 hover:bg-sky-700 text-white text-xs font-bold rounded-xl px-4">
                Start check-in
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 3. Adaptive confidence/motivation message */}
      {adaptive?.message && (
        <div data-testid="adaptive-message"
          className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/15 dark:to-teal-900/10 border border-emerald-200 dark:border-emerald-800 rounded-2xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
              <Heart className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-slate-800 dark:text-slate-200 leading-relaxed">{adaptive.message}</p>
              {adaptive.secondary?.message && (
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5">{adaptive.secondary.message}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stick a Pin In It — opening offload / goal prompt */}
      <StickAPinPrompt role="learner" />

      {/* 5. Today's Focus alignment (for next lesson) */}
      {nextLesson?.id && <LessonFocusCard lessonId={nextLesson.id} lessonLabel={`${nextLesson.pupil_name || "Next lesson"} ${nextLesson.start_time ? "· " + nextLesson.start_time : ""}`} />}

      {/* 4. Lesson Prep card */}
      {pupilData?.next_lesson_focus && !prepDismissed && (
        <LessonPrepCard focus={pupilData.next_lesson_focus} onDismiss={() => setPrepDismissed(true)} />
      )}

      {/* 2. Red Button */}
      {redButton && !redButton.active && (
        <RedButtonCard redButton={redButton} onActivate={() => setRedButton({ ...redButton, active: true })}
          onOnboarded={() => setRedButton({ ...redButton, onboarding_seen: true })} />
      )}
      {redButton?.active && (
        <div data-testid="red-button-active"
          className="bg-red-50 dark:bg-red-900/15 border border-red-200 dark:border-red-800 rounded-2xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-sm font-extrabold text-red-700 dark:text-red-400">Red Button pressed — we hear you</p>
            <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">Your instructor will take it gentler today.</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Red Button Card ──
// `RedButtonCard` is exported so the calm-home entry point
// (`components/learner/CalmRedButtonEntry.jsx`) can mount the SAME
// card in its bottom sheet. Single source of truth — no parallel
// emotional-support UI.
export function RedButtonCard({ redButton, onActivate, onOnboarded }) {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const activate = async () => {
    if (!redButton.onboarding_seen) { setShowOnboarding(true); return; }
    try {
      await axios.post(`${API}/api/red-button/activate`, {}, { withCredentials: true });
      onActivate?.();
      toast.success("Marked — your instructor will see this at lesson prep");
    } catch { toast.error("Failed to activate"); }
  };
  const dismissOnboarding = async () => {
    try { await axios.post(`${API}/api/red-button/dismiss-onboarding`, {}, { withCredentials: true }); } catch {}
    setShowOnboarding(false);
    onOnboarded?.();
  };
  return (
    <>
      <button data-testid="red-button-card" onClick={activate}
        className="w-full bg-white dark:bg-slate-800 border-2 border-red-200 dark:border-red-800 rounded-2xl p-4 flex items-center gap-3 hover:border-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 active:scale-[0.98] transition-all text-left">
        <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
          <div className="w-5 h-5 bg-white rounded-full" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-extrabold text-slate-900 dark:text-white">Not with it today?</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">One tap tells your instructor to take it slower.</p>
        </div>
      </button>
      {showOnboarding && (
        <MobileDialog
          bareContent
          open={showOnboarding}
          onClose={dismissOnboarding}
          testid="red-button-onboarding-wrap"
        >
          <div data-testid="red-button-onboarding" className="bg-white dark:bg-slate-800 w-full max-w-sm rounded-2xl p-6 space-y-3 mx-3">
            <h2 className="text-lg font-extrabold text-slate-900 dark:text-white">Red Button Day</h2>
            <div className="space-y-2 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
              <p>Some days aren't normal. You might feel distracted, overwhelmed, stressed, or just not yourself.</p>
              <p className="text-slate-800 dark:text-slate-200 font-medium">
                Pressing this tells your instructor: "Today isn't a normal learning day — I need a different approach."
              </p>
              <p>Not about avoiding — about being honest.</p>
            </div>
            <Button data-testid="red-onboarding-dismiss" onClick={dismissOnboarding}
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl">Got it</Button>
          </div>
        </MobileDialog>
      )}
    </>
  );
}

// ── Lesson Prep Card ──
function LessonPrepCard({ focus, onDismiss }) {
  const [marking, setMarking] = useState(false);
  const prep = PREP_CONTENT[focus] || { video: null, points: [] };
  const markReady = async () => {
    setMarking(true);
    try {
      await axios.post(`${API}/api/lesson-prep/ready`, {}, { withCredentials: true });
      toast.success("Nice — instructor will know you're prepared");
      onDismiss?.();
    } catch { toast.error("Failed to save"); }
    setMarking(false);
  };
  return (
    <div data-testid="lesson-prep-card"
      className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden">
      <div className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
            <Play className="w-4 h-4 text-violet-600" />
          </div>
          <div className="flex-1">
            <p className="text-[10px] font-bold text-violet-600 uppercase tracking-wider">3-minute prep</p>
            <p className="text-sm font-extrabold text-slate-900 dark:text-white">Today's focus: {focus}</p>
          </div>
        </div>
        {prep.video && (
          <div className="relative w-full rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-900" style={{ paddingBottom: "56.25%" }}>
            <iframe data-testid="prep-video" src={prep.video} title={`${focus} prep`}
              className="absolute inset-0 w-full h-full" frameBorder="0"
              allow="accelerometer; clipboard-write; encrypted-media; picture-in-picture" allowFullScreen />
          </div>
        )}
        {prep.points.length > 0 && (
          <ul className="space-y-1.5 text-xs text-slate-700 dark:text-slate-300">
            {prep.points.map((p, i) => (
              <li key={i} className="flex items-start gap-2"><span className="text-slate-400">—</span>{p}</li>
            ))}
          </ul>
        )}
        <Button data-testid="mark-ready-btn" onClick={markReady} disabled={marking}
          className="w-full h-10 bg-violet-600 hover:bg-violet-700 text-white rounded-xl text-sm font-bold">
          {marking ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1.5" />I'm Ready</>}
        </Button>
      </div>
    </div>
  );
}

// ── Lesson Focus Alignment Card (learner-side) ──
function LessonFocusCard({ lessonId, lessonLabel }) {
  const [loading, setLoading] = useState(true);
  const [focus, setFocus] = useState(null);
  const [selected, setSelected] = useState([]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/reflections/lesson-focus`, {
        params: { lesson_id: lessonId }, withCredentials: true,
      });
      setFocus(data);
      setSelected(data.focus_areas && data.focus_areas.length > 0 ? data.focus_areas : (data.suggested_focus || []));
      setNote(data.note || "");
    } catch {}
    setLoading(false);
  }, [lessonId]);

  useEffect(() => { load(); }, [load]);

  if (loading || !focus) return null;

  const toggle = (skill) => {
    setSelected(s => s.includes(skill) ? s.filter(x => x !== skill) : [...s, skill].slice(0, 4));
  };
  const propose = async (action) => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/reflections/lesson-focus`, {
        lesson_id: lessonId, focus_areas: selected, note, action,
      }, { withCredentials: true });
      toast.success(action === "agree" ? "Agreed" : "Sent to instructor");
      await load();
    } catch { toast.error("Failed"); }
    setSaving(false);
  };

  const iAgreed = focus.my_role === "learner" ? focus.agreed_by_learner : focus.agreed_by_instructor;
  const otherAgreed = focus.my_role === "learner" ? focus.agreed_by_instructor : focus.agreed_by_learner;
  const bothAgreed = focus.agreed_by_learner && focus.agreed_by_instructor;

  return (
    <div data-testid="lesson-focus-card"
      className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/15 dark:to-orange-900/10 border border-amber-200 dark:border-amber-800 rounded-2xl p-4 space-y-3">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
          <Target className="w-4 h-4 text-amber-600" />
        </div>
        <div className="flex-1">
          <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider">Today's Focus</p>
          <p className="text-sm font-extrabold text-slate-900 dark:text-white">{lessonLabel}</p>
        </div>
        {bothAgreed && (
          <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30">
            <Check className="w-3 h-3 text-emerald-600" />
            <span className="text-[10px] font-bold text-emerald-700">Agreed</span>
          </div>
        )}
      </div>

      <p className="text-xs text-slate-500 dark:text-slate-400">
        {focus.has_focus
          ? (focus.proposed_by === focus.my_role ? "You proposed these. Waiting for the other side." : "Your instructor suggested these. Adjust or agree.")
          : "Pick up to 4 areas you want to focus on today."}
      </p>

      <div className="flex flex-wrap gap-1.5">
        {focus.allowed_skills.map(s => {
          const on = selected.includes(s);
          const suggested = focus.suggested_focus.includes(s);
          return (
            <button key={s} data-testid={`focus-chip-${s.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => toggle(s)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-all active:scale-95 ${
                on ? "bg-amber-500 text-white border-amber-500"
                   : suggested ? "bg-white dark:bg-slate-700 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-700"
                   : "bg-white dark:bg-slate-700 text-slate-500 border-slate-200 dark:border-slate-600"
              }`}>
              {s}{suggested && !on && <span className="ml-1 text-[9px]">★</span>}
            </button>
          );
        })}
      </div>

      <textarea data-testid="focus-note" value={note} onChange={e => setNote(e.target.value)}
        placeholder="Optional note — e.g. worried about traffic, want to nail turn-in-the-road..."
        className="w-full h-16 border border-amber-200 dark:border-amber-800 rounded-xl px-3 py-2 text-xs bg-white dark:bg-slate-800 resize-none"
      />

      <div className="grid grid-cols-2 gap-2">
        <Button data-testid="focus-propose" onClick={() => propose("propose")} disabled={saving || selected.length === 0}
          className="h-10 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-xs font-bold">
          {focus.has_focus && focus.proposed_by !== focus.my_role ? "Counter-propose" : "Send to Instructor"}
        </Button>
        <Button data-testid="focus-agree" onClick={() => propose("agree")}
          disabled={saving || !focus.has_focus || iAgreed}
          className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold disabled:opacity-50">
          {iAgreed ? "✓ You agreed" : "Agree with these"}
        </Button>
      </div>
      {focus.has_focus && otherAgreed && !iAgreed && (
        <p className="text-[11px] text-emerald-600 text-center font-bold">Other side has agreed — tap "Agree" to lock it in</p>
      )}
    </div>
  );
}
